import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import Icon from 'shared/components/Icon';
import MetaGrid from 'shared/components/ui/MetaGrid';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import DrawerScrim, { DRAWER_OVERLAY_CLASS } from 'shared/components/ui/DrawerScrim';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import { formatDate, formatRelativeTime } from 'features/agent-analytics/components/runCells';
import Tag from 'features/issues/components/Tag';
import { StatusTag, CategoryTag } from 'features/issues/components/IssueTags';
import FixAction from 'features/issues/components/FixAction';
import { LABEL, RuledSection, DetailText } from 'features/issues/components/DetailParts';
import {
  getIssue, getIssueAgentView, setIssueStatus, mergeIssue, errorText,
} from 'features/issues/api/issuesApi';
import { issuePrompt } from 'features/issues/issuePrompt';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import DrawerTab from 'shared/components/ui/DrawerTab';
import { SpanKindIcon } from 'shared/components/spans/KindTag';
import {
  drawerIconButtonClass,
  tableHeaderCellClass,
  tableDataCellClass,
  tableRowClass,
  tableHeaderRowClass,
} from 'features/activity/styles/dataTableStyles';

const BUTTON = 'inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[11px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-50';
const KIND = { run: 'Run', span: 'Span', conversation: 'Conversation', eval: 'Eval' };
// The icons the Traces tabs and the Evals sidebar use for each kind: runs and
// spans take the trace tree's marks, the rest the app's icon set.
const KIND_ICON = { conversation: 'conversation', eval: 'eval' };

/** What a piece of evidence is, as its icon, named on hover. */
function KindIcon({ kind, size = 14 }) {
  const label = KIND[kind] || kind;
  return (
    <span className="inline-flex shrink-0 text-[color:var(--text-muted)]" title={label} role="img" aria-label={label}>
      {KIND_ICON[kind]
        ? <Icon name={KIND_ICON[kind]} size={size} color="currentColor" />
        : <SpanKindIcon kind={kind === 'span' ? 'span' : 'run'} size={size} color="currentColor" />}
    </span>
  );
}

const FIELD = { title: 'title', root_cause: 'root cause', solution: 'suggested change', category: 'category' };
const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;
const STATUS_WORD = { closed: 'Closed', open: 'Reopened', regression: 'Came back (regression)' };

/** One line per event: what happened to the issue. */
function eventText(ev) {
  const p = ev.payload || {};
  switch (ev.kind) {
    case 'filed': return `Filed${p.evidence ? ` with ${plural(p.evidence, 'piece')} of evidence` : ''}`;
    case 'evidence_added': return `Linked ${plural(p.count ?? 0, 'more piece')} of evidence`;
    case 'status_changed': return STATUS_WORD[p.to] || `${p.from ?? '—'} → ${p.to ?? '—'}`;
    case 'merged': return p.into
      ? `Merged into #${p.into}${p.into_title ? ` “${p.into_title}”` : ''}`
      : `Merged #${p.from}${p.from_title ? ` “${p.from_title}”` : ''} into this${p.evidence_moved ? `, ${plural(p.evidence_moved, 'piece')} of evidence moved` : ''}`;
    case 'edited': {
      const fields = (p.fields || []).map((f) => FIELD[f] || f.replace(/_/g, ' '));
      return `${p.version ? `Version ${p.version}: rewrote` : 'Changed'} the ${fields.join(', ')}`;
    }
    case 'fix_started': return 'Fix started';
    case 'fix_branch': return p.branch ? `Fix branch ${p.branch}` : 'Fix branch pushed';
    case 'note': return p.text || 'Note';
    default: return ev.kind;
  }
}

/**
 * Who did it. Ask writes every issue — from an alert, an eval, the Failures
 * tab or a question — so all of those read as Ask.
 */
function actorLabel(actor = '') {
  const kind = String(actor).split(':')[0];
  return { user: 'A person', heal: 'Coding agent', system: 'Trodo' }[kind] || 'Lucid';
}

/** The traces an event linked, each opening its trace in place. */
function EventTargets({ targets, onOpen }) {
  if (!targets?.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {targets.map((t) => (
        <button
          key={`${t.kind}:${t.id}`}
          type="button"
          onClick={() => onOpen({
            target_kind: t.kind,
            run_id: t.kind === 'run' ? t.id : t.run_id || null,
            span_id: t.kind === 'span' ? t.id : null,
            conversation_id: t.kind === 'conversation' ? t.id : null,
          })}
          className="inline-flex max-w-[220px] items-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-1.5 py-0.5 text-[10.5px] text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
          title={`Open ${KIND[t.kind]?.toLowerCase() || t.kind} ${t.id}`}
        >
          <KindIcon kind={t.kind} size={12} />
          <span className="truncate font-mono">{String(t.id).slice(0, 8)}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * What one piece of evidence shows: the step and the error it hit, and any
 * eval that failed on it (opens that result). The note, when there is
 * nothing else to say.
 */
function EvidenceShows({ e, onOpenEval }) {
  const error = [e.error_type, e.error].filter(Boolean).join(': ');
  const evals = e.eval_failures || [];
  const text = [e.step, error].filter(Boolean).join(' · ') || e.note || (e.in_retention === false ? 'Trace no longer kept' : '—');
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 flex-1 truncate text-[12px] text-[color:var(--text-primary)]" title={text}>{text}</span>
      {evals.slice(0, 1).map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={(ev) => { ev.stopPropagation(); onOpenEval(f); }}
          title={`${f.name} failed${f.reason ? ` · ${f.reason}` : ''}. Open this eval result`}
          className="inline-flex max-w-[40%] shrink-0 items-center gap-1.5 text-[11px] text-[color:var(--feedback-error)] hover:underline"
        >
          <KindIcon kind="eval" size={12} />
          <span className="truncate">{f.name}</span>
        </button>
      ))}
      {evals.length > 1 ? <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">+{evals.length - 1}</span> : null}
    </div>
  );
}

/** A rewrite keeps what the issue said before: shown on request. */
function PreviousText({ before }) {
  const [open, setOpen] = useState(false);
  const fields = Object.entries(before || {}).filter(([k, v]) => k !== 'category' && v);
  if (!fields.length) return null;
  return (
    <div className="mt-1">
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-[11px] text-[color:var(--text-muted)] underline-offset-2 hover:text-[color:var(--text-primary)] hover:underline">
        {open ? 'Hide the previous version' : 'Show the previous version'}
      </button>
      {open ? (
        <div className="mt-2 flex flex-col gap-3 border-l-0 border-t border-[color:var(--border-subtle)] pt-3">
          {fields.map(([k, v]) => (
            <div key={k}>
              <div className={`${LABEL} mb-1`}>{FIELD[k] || k}</div>
              {k === 'title'
                ? <p className="text-[13px] font-medium text-[color:var(--text-primary)]">{v}</p>
                : <DetailText>{v}</DetailText>}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const MENU_W = 320;

/**
 * Pick an issue to fold this one into. The menu is portaled and placed
 * against the button in viewport coordinates, clamped to the window: inside
 * a narrow drawer it would otherwise open off the left edge.
 */
function MergeMenu({ options, onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const w = Math.min(MENU_W, window.innerWidth - 16);
      setPos({ top: r.bottom + 4, left: Math.min(Math.max(8, r.right - w), window.innerWidth - w - 8), width: w });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button type="button" className={BUTTON} disabled={disabled || !options.length} onClick={() => setOpen((o) => !o)}>Merge</button>
      {open && pos ? createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="max-h-72 overflow-y-auto rounded-md border border-[color:var(--border-default)] bg-surface-raised p-1 shadow-token-md"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 1400 }}
        >
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onPick(o); }}
              className="flex w-full flex-col items-start rounded px-3 py-1.5 text-left hover:bg-surface-hover"
            >
              <span className="w-full truncate text-[12px] text-[color:var(--text-primary)]">{o.title}</span>
              <span className="text-[11px] text-[color:var(--text-muted)]">{o.agent_name} · #{o.id}</span>
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

/**
 * One issue: what is wrong and why, the change to make, the runs/spans/
 * conversations it is made of (each opens its trace here), and its history.
 */
export default function IssueDrawer({
  teamId, issueId, initial, onClose, onPrev, onNext, hasPrev = false, hasNext = false, onChanged, openIssues = [],
}) {
  const navigate = useNavigate();
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('evidence');
  const [trace, setTrace] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return getIssue(teamId, issueId)
      .then(setIssue)
      .catch((e) => setError(errorText(e, 'Failed to load issue')))
      .finally(() => setLoading(false));
  }, [teamId, issueId]);

  useEffect(() => { setIssue(null); setTrace(null); setTab('evidence'); load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (trace) { setTrace(null); return; }
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, trace]);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await fn();
      await load();
      onChanged?.(updated);
    } catch (e) {
      setError(errorText(e, 'That did not work'));
    } finally {
      setBusy(false);
    }
  };

  // The issue as a prompt for any coding agent: what it would read over MCP.
  const copyPrompt = async () => {
    setError(null);
    try {
      const view = await getIssueAgentView(teamId, issueId);
      await navigator.clipboard.writeText(issuePrompt(view));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      setError(errorText(e, 'Could not copy'));
    }
  };

  const shown = issue || initial;
  const mergeTargets = useMemo(
    () => openIssues.filter((o) => String(o.id) !== String(issueId) && o.status !== 'merged'),
    [openIssues, issueId],
  );

  const openEvalResult = (f) => navigate(`/${encodeURIComponent(teamId)}/agent-analytics/evals?eval=${encodeURIComponent(f.eval_id)}&run=${encodeURIComponent(f.id)}`);

  const openEvidence = (e) => {
    if (e.target_kind === 'conversation') {
      navigate(`/${encodeURIComponent(teamId)}/agent-analytics/conversations?conversation_id=${encodeURIComponent(e.conversation_id)}`);
      return;
    }
    if (e.run_id) setTrace({ runId: e.run_id, spanId: e.span_id || null });
  };

  return (
    <div className={DRAWER_OVERLAY_CLASS} role="region" aria-label="Issue detail">
      <DrawerScrim onClose={onClose} />
      <section
        className="pointer-events-auto relative flex h-full w-[min(92vw,880px)] flex-col overflow-hidden border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2">
          <div className="flex shrink-0 items-center gap-1">
            <button type="button" onClick={onPrev} disabled={!hasPrev} title="Previous issue" className={drawerIconButtonClass}><ChevronLeft size={16} /></button>
            <button type="button" onClick={onNext} disabled={!hasNext} title="Next issue" className={drawerIconButtonClass}><ChevronRight size={16} /></button>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {shown && shown.status !== 'merged' ? (
              <>
                {canWrite && shown.status !== 'closed' ? (
                  <FixAction teamId={teamId} teamSlug={teamId} issue={shown} onSessionsChanged={load} />
                ) : null}
                <button type="button" className={BUTTON} onClick={copyPrompt}>{copied ? 'Copied' : 'Copy prompt'}</button>
                {canWrite && (
                  <MergeMenu
                    options={mergeTargets}
                    disabled={busy}
                    onPick={(o) => act(() => mergeIssue(teamId, issueId, o.id))}
                  />
                )}
                {!canWrite ? null : shown.status === 'closed' ? (
                  <button type="button" className={BUTTON} disabled={busy} onClick={() => act(() => setIssueStatus(teamId, issueId, 'open'))}>Reopen</button>
                ) : (
                  <button type="button" className={BUTTON} disabled={busy} onClick={() => act(() => setIssueStatus(teamId, issueId, 'closed'))}>Mark closed</button>
                )}
              </>
            ) : null}
            <button type="button" onClick={onClose} aria-label="Close" className={drawerIconButtonClass}><X size={16} /></button>
          </div>
        </div>

        {loading && !shown ? (
          <div className="flex flex-1 items-center justify-center"><TrodoLoader label="Loading issue" /></div>
        ) : !shown ? (
          <div className="m-4 rounded-md border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">{error || 'Issue not found.'}</div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-6 pt-2">
            {error ? (
              <div className="rounded-md border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-2 text-[12px] text-[color:var(--feedback-error)]">{error}</div>
            ) : null}

            <div className="shrink-0">
              <div className="flex flex-wrap items-center gap-1">
                <StatusTag status={shown.status} />
                <CategoryTag category={shown.category} />
                {shown.version > 1 ? <Tag title="Rewritten as more was learned; every version is on the timeline">v{shown.version}</Tag> : null}
              </div>
              <h2 className="mt-2.5 break-words text-[17px] font-semibold leading-snug tracking-[-0.01em] text-[color:var(--text-primary)]">{shown.title}</h2>
              <MetaGrid
                className="mt-4"
                cells={[
                  { label: 'Agent', content: shown.agent_name || '—' },
                  { label: 'Evidence', content: <span className="tabular-nums">{(shown.evidence_count ?? 0).toLocaleString()}</span> },
                  { label: 'First seen', title: formatDate(shown.first_seen_at), content: formatRelativeTime(shown.first_seen_at) },
                  { label: 'Last seen', title: formatDate(shown.last_seen_at), content: formatRelativeTime(shown.last_seen_at) },
                  shown.status === 'merged' && shown.merged_into_id && {
                    label: 'Merged into',
                    content: (
                      <button type="button" onClick={() => navigate(`?issue=${encodeURIComponent(shown.merged_into_id)}`)} className="text-left underline-offset-2 hover:underline">
                        #{shown.merged_into_id}
                      </button>
                    ),
                  },
                ].filter(Boolean)}
              />
            </div>

            <RuledSection title="Root cause">
              <DetailText>{shown.root_cause}</DetailText>
            </RuledSection>
            {shown.solution ? (
              <RuledSection title="Suggested change">
                <DetailText>{shown.solution}</DetailText>
              </RuledSection>
            ) : null}

            <div className="-mx-4 flex shrink-0 flex-col">
              {/* The shared drawer tab, so this strip matches the run drawer
                  an evidence row opens over it. */}
              <div className="flex items-center gap-1 border-b border-[color:var(--border-default)] px-2" role="tablist">
                {[['evidence', 'Evidence', issue ? issue.evidence?.length ?? 0 : null], ['timeline', 'Timeline', null]].map(([id, label, count]) => (
                  <DrawerTab
                    key={id}
                    id={id}
                    role="tab"
                    label={label}
                    badge={count != null ? count.toLocaleString() : null}
                    active={tab === id}
                    onClick={setTab}
                  />
                ))}
              </div>

              {!issue ? (
                <div className="flex h-32 items-center justify-center"><TrodoLoader label="Loading" /></div>
              ) : tab === 'evidence' ? (
                issue.evidence.length === 0 ? (
                  <div className="flex h-24 items-center justify-center text-[12px] text-[color:var(--text-muted)]">No evidence.</div>
                ) : (
                  <div className="mx-4 mt-3 overflow-x-auto rounded-md border border-[color:var(--border-default)]">
                    <table className="w-full min-w-[480px] table-fixed text-left">
                      <colgroup>
                        <col style={{ width: '56px' }} />
                        <col style={{ width: '96px' }} />
                        <col />
                      </colgroup>
                      <thead>
                        <tr className={tableHeaderRowClass}>
                          <th className={tableHeaderCellClass}>Kind</th>
                          <th className={tableHeaderCellClass}>When</th>
                          <th className={tableHeaderCellClass}>What it shows</th>
                        </tr>
                      </thead>
                      <tbody>
                        {issue.evidence.map((e) => {
                          const openable = e.target_kind === 'conversation' || Boolean(e.run_id);
                          return (
                            <tr
                              key={e.id}
                              className={openable ? tableRowClass : 'border-b border-[color:var(--border-subtle)] opacity-70 last:border-b-0'}
                              onClick={() => openable && openEvidence(e)}
                              title={openable ? 'Open trace' : 'This trace is no longer kept'}
                            >
                              <td className={tableDataCellClass}><KindIcon kind={e.target_kind} /></td>
                              <td className={tableDataCellClass}>
                                <span className="tabular-nums text-[11px] text-[color:var(--text-secondary)]" title={formatDate(e.occurred_at || e.added_at)}>
                                  {formatRelativeTime(e.occurred_at || e.added_at)}
                                </span>
                              </td>
                              <td className={tableDataCellClass}>
                                <EvidenceShows e={e} onOpenEval={openEvalResult} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <div className="mx-4 mt-3 overflow-x-auto rounded-md border border-[color:var(--border-default)]">
                  <table className="w-full min-w-[480px] table-fixed text-left">
                    <colgroup><col style={{ width: '96px' }} /><col /><col style={{ width: '110px' }} /></colgroup>
                    <thead>
                      <tr className={tableHeaderRowClass}>
                        <th className={tableHeaderCellClass}>When</th>
                        <th className={tableHeaderCellClass}>Event</th>
                        <th className={tableHeaderCellClass}>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...issue.events].reverse().map((ev) => {
                        const p = ev.payload || {};
                        const why = p.reason || p.context?.reason;
                        return (
                          <tr key={ev.id} className="border-b border-[color:var(--border-subtle)] align-top last:border-b-0">
                            <td className={tableDataCellClass}>
                              <span className="tabular-nums text-[11px] text-[color:var(--text-muted)]" title={formatDate(ev.at)}>{formatRelativeTime(ev.at)}</span>
                            </td>
                            <td className={tableDataCellClass}>
                              <div className="truncate text-[12px] text-[color:var(--text-primary)]" title={why ? `${eventText(ev)} · ${why}` : undefined}>
                                {eventText(ev)}{why ? <span className="text-[color:var(--text-muted)]"> · {why}</span> : null}
                              </div>
                              {p.context?.eval_id ? (
                                <button type="button" onClick={() => navigate(`/${encodeURIComponent(teamId)}/agent-analytics/evals?eval=${encodeURIComponent(p.context.eval_id)}`)} className="mt-0.5 inline-flex items-center gap-1.5 text-[11px] text-[color:var(--text-muted)] underline-offset-2 hover:text-[color:var(--text-primary)] hover:underline">
                                  <KindIcon kind="eval" size={12} />
                                  From an eval
                                </button>
                              ) : null}
                              <EventTargets targets={p.targets} onOpen={openEvidence} />
                              {ev.kind === 'edited' ? <PreviousText before={p.before} /> : null}
                            </td>
                            <td className={tableDataCellClass}><span className="truncate text-[11px] text-[color:var(--text-muted)]">{actorLabel(ev.actor)}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {trace?.runId ? (
          <AgentRunDetailPage
            teamId={teamId}
            runId={trace.runId}
            initialSpanId={trace.spanId || undefined}
            contained
            onClose={() => setTrace(null)}
            onChange={() => {}}
          />
        ) : null}
      </section>
    </div>
  );
}
